import React, { useContext, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Grid
} from "@mui/material";
import PregnancyConditions from "../containers/Emotion/PregnancyCondtions";
import T1ActivityIntensity from "../containers/Emotion/T1ActivityIntensity";
import { useSelector } from "react-redux";
import { store } from "../redux/store";
import * as patientActions from "../redux/actions/patientActions";
import Scrollbar from "../containers/Anatomy/Scrollbar";
import EmotionChart from "../containers/Charts/EmotionChart";
import PregnancyRecommendations from "../containers/Emotion/PregnancyRecommendations";
import { ConsultationContext } from "../context/ConsultationContext";
import usePermissions from "../utils/permissions";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const menuStyle = {
  '&:hover': {
      backgroundColor: '#D3D3D3',
      color: 'black',
  },
  '&.Mui-selected': {
      backgroundColor: 'grey',
      color: '#fff',
  },
  '&.Mui-selected:hover': {
      backgroundColor: 'grey',
  },

  '&.Mui-focusVisible': {
      backgroundColor: 'grey',
  },
  '&.Mui-selected.Mui-focusVisible': {
      backgroundColor: 'grey',
  },
}

function mergeAnswersWithSchedules(item) {
  const answers = Array.isArray(item?.answers) ? item.answers : [];
  const schedules = Array.isArray(item?.consultationSchedules)
    ? item.consultationSchedules
    : [];

  const answersMap = new Map(
    answers?.map((a) => [
      Number(a.assessmentWeek),
      { ...a, week: Number(a.assessmentWeek) },
    ])
  );

  const mergedAnswers = schedules?.map((s) => {
    const wk = Number(s.week);
    if (answersMap.has(wk)) {
      return answersMap.get(wk);
    }
    return {
      week: wk,
      trimester: s.trimester,
      answer: null,
      minValue: 0,
      maxValue: 10,
    };
  });

  return { ...item, answers: mergedAnswers };
}

const Emotion = () => {
  const { patientId, consultationId } = useParams();
  const patientReducer = useSelector((state) => state.patientReducer);
  const emotionCharts = patientReducer?.stressChart;
  const stressChart = mergeAnswersWithSchedules(emotionCharts?.[0]);
  const physicalFitness = patientReducer?.physicalFitness;

  const [stressLevel, setStressLevel] = useState(stressChart?.recommendation);
  const [updateData, setUpdateData] = useState({
  })
  const [edit, setEdit] = useState(true);
  const location = useLocation();

  const { setChildData } = useContext(ConsultationContext);
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const mode = queryParams.get("mode");
    if (mode) {
      setEdit(mode === "view" ? false : hasPermission("LEP_CREATE") ? true: false);
    }
  }, [location.search]);

  useEffect(() => {
    setUpdateData({
      "stressNotes": null,
      "stressRecommendations": null,
      "physicalFitnessNotes": null,
      "canBeActive": false,
      "conditions": {},
      "recommendedActivities": [],
      "workLevel": null,
      "travelLevel": null,
      "leisureLevel": null
    });
  }, [consultationId]);

  const convertLevels = (arr) => {
    const result = {};

    arr.forEach(item => {
      const selected = item.current.find(c => c.isSelected)?.intensity;

      if (item.activityType === "Work Related") {
        result.workLevel = selected || null;
      }
      if (item.activityType === "Travel Related") {
        result.travelLevel = selected || null;
      }
      if (item.activityType === "Leisure & Recreational Activity") {
        result.leisureLevel = selected || null;
      }
    });

    return result;
  };

  useEffect(() => {
    if (!physicalFitness || Object.keys(physicalFitness).length === 0) {
      setUpdateData({
        ...updateData,
        "physicalFitnessNotes": null,
        "canBeActive": false,
        "conditions": {},
        "recommendedActivities": [],
        "workLevel": null,
        "travelLevel": null,
        "leisureLevel": null
      });
      return;
    }

    if (physicalFitness?.conditions) {
      if (physicalFitness?.conditions?.absolute) {
        setUpdateData(prev => ({
          ...prev,
          conditions: {
            ...prev.conditions,
            absolute: [...(prev.conditions.absolute || []), ...physicalFitness?.conditions?.absolute.filter(item => item.isSelected)]
          }
        }));
      }
      if (physicalFitness?.conditions?.relative) {
        setUpdateData(prev => ({
          ...prev,
          conditions: {
            ...prev.conditions,
            relative: [...(prev.conditions.relative || []), ...physicalFitness?.conditions?.relative.filter(item => item.isSelected)]
          }
        }));
      }
    }

    if (physicalFitness?.recommendations?.activities) {
      setUpdateData(prev => ({
        ...prev,
        recommendedActivities: physicalFitness?.recommendations?.activities.filter(item => item.current)?.map(item => ({
          name: item.name
        }))
      }));
    }

    if (physicalFitness?.recommendations?.intensity) {
      setUpdateData(prev => ({
        ...prev,
        ...convertLevels(physicalFitness?.recommendations?.intensity)
      }));
    }

    if (physicalFitness?.canBeActive) {
      setUpdateData(prev => ({
        ...prev,
        canBeActive: physicalFitness?.canBeActive
      }));
    }

    if (physicalFitness?.physicalFitnessNotes) {
      setUpdateData(prev => ({
        ...prev,
        physicalFitnessNotes: physicalFitness?.physicalFitnessNotes
      }));
    }

  }, [physicalFitness]);

  useEffect(() => {
    if (patientReducer.emotionData?.recommendation) {
      setUpdateData(prev => ({
        ...prev,
        stressRecommendations: patientReducer.emotionData?.recommendation.find((item) => item.isSelected)?.label
      }));
    }
    if (patientReducer.emotionData?.notes) {
      setUpdateData(prev => ({
        ...prev,
        stressNotes: patientReducer.emotionData?.notes
      }));
    }
  }, [patientReducer])

  function handleStressLevelChange(event) {
    setUpdateData(prev => ({
      ...prev,
      stressRecommendations: event.target.value
    }));
  }

  useEffect(() => {
    if (patientId) {
      store.dispatch(patientActions.getEmotionChartsRequestAction(patientId));
      store.dispatch(patientActions.getPhysicalFitnessRequestAction(patientId));
      store.dispatch(patientActions.getEmotionDataRequestAction(patientId));
    }
  }, [patientId]);

  useEffect(() => {
    setChildData(updateData)
  }, [updateData]);

  if (patientReducer.requestState) {
    return (
      <Box
        sx={{
          width: "40%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
          flex: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // if (patientReducer.error) {
  //   return (
  //     <Box
  //       sx={{
  //         width: "40%",
  //         display: "flex",
  //         justifyContent: "center",
  //         alignItems: "center",
  //         height: 300,
  //         flex: 1,
  //       }}
  //     >
  //       <Typography color="error">Error loading data</Typography>
  //     </Box>
  //   );
  // }
  const handleConditions = (type, item) => {
    setUpdateData(prev => {
      const list = prev.conditions[type]; // absolute or relative

      const exists = list.some(cond => cond.name === item.name);

      const updatedList = exists
        ? list.filter(cond => cond.name !== item.name)   // Remove if exists
        : [...list, { name: item.name }]                              // Add if not exists

      return {
        ...prev,
        conditions: {
          ...prev.conditions,
          [type]: updatedList
        }
      };
    });
  };

  const handleUpdateData = (data, type) => {
    if (type == "table") {
      setUpdateData(prev => ({
        ...prev,
        recommendedActivities: data.filter(item => item.col2).map(item => ({
          name: item.col1
        }))
      }));
    }
    else if (type == "notes") {
      setUpdateData(prev => ({
        ...prev,
        physicalFitnessNotes: data
      }));
    }
    else if (type == "Work Related") {
      setUpdateData(prev => ({
        ...prev,
        workLevel: data.target.value
      }));
    }
    else if (type == "Travel Related") {
      setUpdateData(prev => ({
        ...prev,
        travelLevel: data.target.value
      }));
    }
    else if (type == "Leisure & Recreational Activity") {
      setUpdateData(prev => ({
        ...prev,
        leisureLevel: data.target.value
      }));
    }

  }
const renderContent=()=>{
  return <>
   <Box display={"flex"} flex={1} width={"100%"}>
            <Box
              width={"100%"}
              height={"100%"}
              sx={{ marginRight: 2 }}
              display={"flex"}
              flexDirection={"column"}
            >
              <Typography
                align="left"
                sx={{ marginLeft: 2, fontSize: "15px", fontWeight: "bold" }}
              >
                Stress
              </Typography>

              <Paper
                elevation={5}
                sx={{
                  display: "flex",
                  flex: 1,
                  flexDirection: {xs: "column", md: "row"},
                  height: {xs: "auto", md: "180px"},
                  // backgroundColor: "rgba(256, 256, 256, 0.3)",
                  ml: {xs: 0, md: 1},
                  my: 1,
                  borderRadius: "10px",
                  p: 1,
                  justifyContent: "space-between",
                  boxShadow:'none',
                  border:"1px solid #E5E7EB"
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    // flex: 1,
                    flexDirection: "row",
                    mt: 1,
                    width: {xs: "100%", md: "65%"},
                  }}
                >
                  <EmotionChart stressChart={emotionCharts} page="Emotion" />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    // flex: 1,
                    width: {xs: "100%", md: "20%"},
                    marginLeft: {xs: 0, md: 1},
                    marginRight: {xs: 0, md: 1},
                    mt: {xs: 2, md: 0},
                  }}
                >
                  <Typography align="left" variant="p" fontSize={"14px"}>
                    Recommendation:
                  </Typography>
                  <Select
                    value={(patientReducer.emotionData?.recommendation || []).some(d => d.label === updateData?.stressRecommendations) ? updateData?.stressRecommendations : ''}
                    onChange={handleStressLevelChange}
                    displayEmpty
                    disabled={!edit}
                    sx={{
                      borderRadius: "50px",
                      backgroundColor: "#f9f9f9",
                      boxShadow: "0 0 0 1px #ddd",
                      // px: 2,
                      // py: 0.5,
                      height: "18px",
                      width: "130px",
                      fontSize: "14px",
                      fontWeight: 500,
                      my: 1,
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      },
                      "& fieldset": { border: "none" },
                    }}
                  >
                    {patientReducer.emotionData?.recommendation?.map((data, index) => {
                      return <MenuItem value={data.label} sx={menuStyle}>{data.label}</MenuItem>;
                    })}
                  </Select>
                  <Typography align="left" variant="p" fontSize={"14px"}>
                    Notes:
                  </Typography>
                  <TextField
                    multiline
                    disabled={!edit}
                    value={updateData?.stressNotes}
                    onChange={(e) => {
                      setUpdateData(prev => ({
                        ...prev,
                        stressNotes: e.target.value
                      }))
                    }}
                    minRows={5}
                    placeholder="Type your text here..."
                    variant="outlined"
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "5px",
                        // backgroundColor: "#f9fcfd",
                        // boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        padding: "8px 12px",
                        "& fieldset": {
                          border: "1px solid #E5E7EB",
                        },
                        fontSize: "14px",
                        marginTop: 1,
                      },
                    }}
                  />
                </Box>
              </Paper>
            </Box>
          </Box>

          <Box
            display={"flex"}
            flex={1}
            flexDirection={"column"}
            sx={{ mb: 1 }}
          >
            <Typography
              align="left"
              sx={{ marginLeft: 2, fontSize: "15px", fontWeight: "bold", marginTop: 1 }}
            >
              Physical Fitness
            </Typography>

            <Paper
              elevation={5}
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                // backgroundColor: "rgba(256, 256, 256, 0.3)",
                m: {xs: 0, md: 1},
                mt: {xs: 2, md: 1},
                borderRadius: "10px",
                marginBottom: 2,
                minWidth: 0,
                overflowX: "auto",
                border:"1px solid #E5E7EB", boxShadow:'none'
              }}
            >
              <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minWidth: 0 }}>
                <Box sx={{ display: "flex", flex: 1, width: "100%", minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      fontSize: "14px",
                      alignItems: "center",
                      pt: 2,
                      mr: {xs: 0, md: 5},
                      // width: "100%",
                      // minWidth: 0,
                      // overflowX: "auto",
                    }}
                  >
                    <T1ActivityIntensity data={physicalFitness} />
                  </Box>
                </Box>

                <Grid
                  container
                  spacing={1}
                  sx={{ width: "100%", height: "100%" }}
                >

                  <Grid size={{ xs: 12, md: 12, xl: 5, lg:12 }}>
                    <Paper
                      elevation={3}
                      sx={{
                        // backgroundColor: "rgba(256,256,256,0.4)",
                        // boxShadow: "0px 4px 4px rgba(0,0,0,0.15)",
                        borderRadius: "10px",
                        p: 1,
                        m: 1,
                        height: {md: "93%" ,xs:'95%'},
                        border:"1px solid #E5E7EB",
                        boxShadow:'none'
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                        <Typography sx={{ pl: 2, fontSize: 14, fontWeight: 500, textAlign:'left' }}>
                          Conditions
                        </Typography>

                        <Grid container spacing={1}>
                          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TableContainer sx={{ overflowX: "auto" }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow
                                    sx={{
                                      "& td, & th": {
                                        border: 0,
                                        py: 1,
                                        lineHeight: 1.2,
                                        fontSize: "13px",
                                      },
                                    }}
                                  >
                                    <TableCell>
                                      <Box
                                        sx={{
                                          backgroundColor: "#DDEEFD",
                                          p: 1,
                                          borderRadius: "10px",
                                          display: "inline-block",
                                          fontSize: '13px',
                                          fontFamily: 'Roboto Flex'
                                        }}
                                      >
                                        Absolute
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {physicalFitness?.conditions?.absolute.map((item, key) => (
                                    <TableRow
                                      key={item.name}

                                      onClick={() => { edit && handleConditions("absolute", item) }}
                                      sx={{
                                        cursor: "pointer",

                                        "& td, & th": {
                                          border: 0,
                                          lineHeight: 1,
                                          fontSize: "13px",
                                          lineHeight: 1.2,
                                          paddingY: 0.2,
                                        },
                                      }}
                                    >
                                      <TableCell>
                                        <Box
                                          component="span"
                                          sx={{
                                            border: item.name == updateData?.conditions?.['absolute']?.find(
                                              (x) => x.name === item.name
                                            )?.name ? "1px solid #919191":'transparent',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: "6px",
                                            display: "inline-block",
                                          }}
                                        >
                                          {item.name}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TableContainer sx={{ overflowX: "auto" }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow
                                    sx={{
                                      "& td, & th": {
                                        border: 0,
                                        py: 1,
                                        lineHeight: 1.2,
                                        fontSize: "13px",
                                      },
                                    }}
                                  >
                                    <TableCell>
                                      <Box
                                        component="span"
                                        sx={{
                                          backgroundColor: "#DDEEFD",
                                          p: 1,
                                          borderRadius: "10px",
                                          display: "inline-block",
                                          fontSize: '13px',
                                          fontFamily: 'Roboto Flex'
                                        }}
                                      >
                                        Relative
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {physicalFitness?.conditions?.relative.map((item, key) => (
                                    <TableRow
                                      key={item.name}
                                      onClick={() => { edit && handleConditions("relative", item) }}
                                      sx={{
                                        cursor: "pointer",

                                        "& td, & th": {
                                          border: 0,
                                          lineHeight: 1,
                                          fontSize: "13px",
                                          lineHeight: 1.2,
                                          paddingY: 0.2,
                                        },
                                      }}
                                    >
                                      <TableCell>
                                        <Box
                                          component="span"
                                          sx={{
                                            border: item.name == updateData?.conditions?.['relative']?.find(
                                              (x) => x.name === item.name
                                            )?.name ? "1px solid #919191":'transparent',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: "6px",
                                            display: "inline-block",
                                          }}
                                        >
                                          {item.name}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Grid>
                        </Grid>

                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mt: 2,
                            gap: 1,
                            mb:2
                          }}
                        >
                          <Typography sx={{ fontSize: 14 }}>
                            Mother can be physically active during pregnancy
                          </Typography>

                          <ToggleButtonGroup
                            value={updateData?.canBeActive ? "yes" : "no"}
                            exclusive
                            disabled={!edit}
                            onChange={(e) =>
                              setUpdateData((prev) => ({
                                ...prev,
                                canBeActive: e.target.value === "yes",
                              }))
                            }
                            sx={{
                              "& .MuiToggleButton-root": {
                                border: "none",
                                textTransform: "none",
                                px: 4,
                                py: 0,
                                borderRadius: "5px",
                                "&:hover": {
                                  backgroundColor: "transparent",
                                },
                              },
                              "& .Mui-selected": {
                                border: "1px solid #919191",
                                color: "black",
                              },
                            }}
                          >
                            <ToggleButton value="yes">Yes</ToggleButton>
                            <ToggleButton value="no">No</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {updateData?.canBeActive && (
                    <Grid size={{ xs: 12, md: 12, lg: 12, xl: 7 }}>
                      <Paper
                        elevation={3}
                        sx={{
                          // backgroundColor: "rgba(256,256,256,0.3)",
                          // boxShadow: "0px 4px 4px rgba(0,0,0,0.15)",
                          borderRadius: "10px",
                          p: 1,
                          height: {xl:"93%",lg:'96%'},
                          m: 1,
                          border:"1px solid #E5E7EB", boxShadow:'none'
                        }}
                      >
                        <PregnancyRecommendations
                          data={physicalFitness}
                          updateData={updateData}
                          handleUpdateData={handleUpdateData}
                        />
                      </Paper>
                    </Grid>
                  )}

                </Grid>
              </Box>
            </Paper>
          </Box>
  </>
}
  return (
    <Box
      sx={{
        py: 1,
        px: {xs: 1, md: 2},
        height: {xl:"84vh", lg:'84vh', md:'78vh', xs: "auto"},
        overflow: {xs: "hidden", md: "auto"},
        marginBottom: 2,
        display: "flex",
        flex: 1,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          // backdropFilter: "blur(10px)",
          // WebkitBackdropFilter: "blur(10px)",
          // backgroundColor: "#E0F3F9",
          borderRadius: 4,
          border:"1px solid #E5E7EB",
          textAlign: "center",
          py: 2,
          px: 1,
          overflow: "hidden",
          overflowY: {xs: "hidden", md: "auto"},
          display: "flex",
          flex: 1,
          flexDirection: "column",
          boxShadow:'none'
        }}
      >
        {isMobile ?
          renderContent() :
          <Scrollbar
            style={{ height: "100%" }}
            gridTemplate="auto / 1fr 20px"
            padding="0 0rem"
            scrollerPadding="0rem"
            scrollerWidth="12px"
          >
            {renderContent()}
          </Scrollbar>}
      </Paper>
    </Box>
  );
};

export default Emotion;
